/**
 * Plain words for the pipeline view (/admin/pipeline). Step ids are the
 * pipeline's own stage ids (gemini-cv-project docs/PROGRESS_FILE.md) and the
 * worker's steps (worker/deepthird_worker/live.py); an id not listed here is
 * shown as it is.
 */
import type { PipelineJob, PipelineProgress, PipelineStep, ProgressCounter, VideoState } from "../api/types";
import { fmtBytes } from "./format";

export const STEP_LABEL: Record<string, string> = {
  download: "Download",
  autocal: "Calibration",
  end_canary: "Batting-end canary",
  zn_gate: "ZN gate",
  scene_gate: "Scene gate",
  gate_stubs: "Candidate windows",
  detector_peaks: "Ball detection",
  m10_trajfit: "Trajectory fit",
  m09_events: "Events",
  zn_buffer_verify: "Verify",
  batting_end_check: "Batting-end check",
  m12_bat_swing: "Bat swing",
  m13_vertical: "Vertical axis",
  m14_players: "Players",
  m09_video_length: "Length per video",
  m15_speeds: "Speeds",
  results: "Results",
  pictures: "Clip pictures",
  upload: "Upload",
  m03_candidates: "Candidates",
  mb_yolo_score: "Candidate scores",
  m07_track: "Tracks",
  m08_delivery: "Deliveries",
};

/** The phase a worker without step ids posts. */
const PHASE_LABEL: Record<string, string> = {
  ingest_check: "Ingest",
  autocal: "Calibration",
  gate: "ZN gate",
  bank: "Ball detection",
  fit: "Trajectory fit",
  events: "Events",
  verify: "Verify",
  bat_swing: "Bat swing",
  vertical: "Vertical axis",
  export: "Export",
  thumbnails: "Clip pictures",
  players: "Players",
  manifest: "Finishing",
  transfer_in: "Download",
  transfer_out: "Upload",
};

const PART_LABEL: Record<string, string> = {
  motion: "motion pass",
  embed: "appearance pass",
  states: "state model",
  chunks: "chunks",
  shards: "shards",
  gate: "gate",
  stubs: "windows",
  peaks: "ball detection",
  fit: "fit",
  vote: "vote",
  stumps: "stump search",
  hotspot: "people-guided stump search",
  anchors: "bounce anchors",
  stump: "stump ruler",
  solve: "solve",
  bowler: "bowlers",
  striker: "batsmen",
  grouping: "grouping",
};

const STATE_WORDS: Record<VideoState, string> = {
  uploading: "Uploading",
  uploaded: "Uploaded",
  ingesting: "Ingest",
  ingest_failed: "Ingest failed",
  calibration_queue: "Waiting for calibration",
  calibrating: "Calibrating",
  unusable: "Unusable",
  calibrated: "Calibrated",
  queued: "Waiting for analysis",
  processing: "Analysing",
  held: "Held for review",
  ready: "Ready",
  failed: "Failed",
  expired: "Expired",
  deleted: "Deleted",
};

const TASK_WORDS: Record<string, string> = {
  ingest: "Ingest",
  calibration_check: "Camera check",
  autocal: "Calibration",
  analyze_video: "Analysis",
  thumbnails: "Clip pictures",
  export: "Export",
  identify_players: "Players",
};

/** Tasks that take a video through the pipeline; the others follow a finished analysis (an export, pictures, a players backfill). */
const PIPELINE_TASKS = new Set(["ingest", "calibration_check", "autocal", "analyze_video"]);

/**
 * The job a video's pipeline page follows: one running now, else the newest
 * pipeline job, else the newest. A finished export or backfill never hides the
 * analysis's steps.
 */
export function mainJob<J extends { task: string; state: string }>(jobs: J[]): J | null {
  return jobs.find((j) => j.state === "running") ?? jobs.find((j) => PIPELINE_TASKS.has(j.task)) ?? jobs[0] ?? null;
}

export const stepLabel = (id: string) => STEP_LABEL[id] ?? id;
export const phaseLabel = (id: string) => PHASE_LABEL[id] ?? id;
export const stateWords = (s: VideoState) => STATE_WORDS[s] ?? s;
export const taskWords = (t: string) => TASK_WORDS[t] ?? t;

/** "motion pass", or for a stage the canary ran on its behalf "ZN gate · motion pass". */
export function partLabel(part: string | null | undefined): string {
  if (!part) return "";
  const [head, ...rest] = part.split(" ");
  if (rest.length && STEP_LABEL[head!]) return [STEP_LABEL[head!], PART_LABEL[rest.join(" ")] ?? rest.join(" ")].join(" · ");
  if (STEP_LABEL[part]) return STEP_LABEL[part];
  return PART_LABEL[part] ?? part.replace(/_/g, " ");
}

export const num = (n: number) => Math.round(n).toLocaleString("en-US");

/** "28 Sep, 06:49" */
export const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** "145,000 of 301,544 frames" */
export function counterText(c: ProgressCounter | null | undefined): string {
  if (!c) return "";
  if (c.unit === "bytes") return c.total != null ? `${fmtBytes(c.completed)} of ${fmtBytes(c.total)}` : fmtBytes(c.completed);
  const unit = c.unit === "seconds_of_video" ? "s of video" : c.unit;
  return c.total != null ? `${num(c.completed)} of ${num(c.total)} ${unit}` : `${num(c.completed)} ${unit}`;
}

export function counterShare(c: ProgressCounter | null | undefined): number | null {
  if (!c || c.total == null || c.total <= 0) return null;
  return Math.max(0, Math.min(1, c.completed / c.total));
}

/** "39 min", "63 s", "1 h 07 min" */
export function fmtSeconds(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return "";
  if (s < 90) return `${Math.round(s)} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
}

export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export const secondsSince = (iso: string | null | undefined, now = Date.now()) => (iso ? (now - new Date(iso).getTime()) / 1000 : Infinity);

/** The worker posts at least every 30 s while a step changes and heartbeats every 60 s; past 2 min it may be gone. */
export const STALE_S = 120;

export type Where = { label: string; dot: "" | "on" | "ok" | "bad"; detail: string };

/** Where a video is, in one line: the running step, the queue, or the video's state. */
export function whereIs(state: VideoState, job: PipelineJob | null, progress: PipelineProgress | null, attemptState?: string | null): Where {
  const running = job?.state === "running" && (attemptState === "running" || attemptState === "leased" || attemptState == null);
  if (running) {
    const label = progress?.step ? stepLabel(progress.step) : progress?.phase ? phaseLabel(progress.phase) : taskWords(job!.task);
    return { label, dot: "on", detail: taskWords(job!.task) + (job!.attempt > 1 ? ` · attempt ${job!.attempt}` : "") };
  }
  if (job && (job.state === "created" || job.state === "dispatched")) {
    const where = job.queue_position ? `${ordinal(job.queue_position)} in the ${job.pool.toUpperCase()} queue` : "queued";
    return { label: `Waiting · ${taskWords(job.task)}`, dot: "", detail: where };
  }
  const bad = state === "failed" || state === "ingest_failed" || state === "unusable";
  const ok = state === "ready";
  return { label: stateWords(state), dot: bad ? "bad" : ok ? "ok" : state === "held" ? "bad" : "", detail: job?.error ? `${job.error.code}` : "" };
}

export function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export function stepDot(s: PipelineStep): "" | "on" | "ok" | "bad" {
  return s.state === "running" ? "on" : s.state === "ok" ? "ok" : s.state === "failed" ? "bad" : "";
}
